import { cn } from '@/shared/lib/utils';
import type { ReactNode } from 'react';

export interface DataTableColumn<Row> {
  key: string;
  header: string;
  /** 고정 폭(px). 없으면 남는 폭을 나눈다 */
  width?: number;
  render: (row: Row) => ReactNode;
}

interface DataTableProps<Row> {
  columns: DataTableColumn<Row>[];
  rows: Row[];
  getRowKey: (row: Row) => string;
  /** 강조할 행 (기준 월 등) */
  isRowSelected?: (row: Row) => boolean;
  emptyMessage: string;
  /**
   * Figma "Table 1" — 1px line-grey 테두리 + radius 16 상자.
   * 기본(false)은 상세 드로어처럼 테두리 없이 머리행만 둥근 회색.
   */
  bordered?: boolean;
}

/**
 * Figma Table / Cell — 회색 머리행(Cell 헤더: SB 14 · tertiary) + 본문 행(Cell 기본: M 14).
 * 셀 padding 12 / 16. 거래상태 셀은 Badge, 담당자 셀은 TableUserCell 을 render 에서 쓴다.
 */
export function DataTable<Row>({
  columns,
  rows,
  getRowKey,
  isRowSelected,
  emptyMessage,
  bordered = false,
}: DataTableProps<Row>) {
  const table = (
    <table className="w-full min-w-[440px] table-fixed border-separate border-spacing-0 text-left">
      <colgroup>
        {columns.map((column) => (
          <col
            key={column.key}
            style={column.width ? { width: column.width } : undefined}
          />
        ))}
      </colgroup>
      <thead>
        <tr>
          {columns.map((column) => (
            <th
              key={column.key}
              scope="col"
              className={cn(
                'bg-surface-grey px-3 py-4 text-body-14 font-semibold text-fg-tertiary',
                !bordered && 'first:rounded-l-16 last:rounded-r-16',
              )}
            >
              {column.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td
              colSpan={columns.length}
              className="px-3 py-8 text-center text-body-14 font-medium text-fg-light"
            >
              {emptyMessage}
            </td>
          </tr>
        ) : (
          rows.map((row) => {
            const selected = isRowSelected?.(row) ?? false;
            return (
              <tr key={getRowKey(row)} aria-selected={selected || undefined}>
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={cn(
                      'px-3 py-4 text-body-14 font-medium text-fg-primary',
                      selected &&
                        'bg-primary-100 first:rounded-l-16 last:rounded-r-16',
                    )}
                  >
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  );

  // 좁은 화면(모바일 드로어)에서는 표만 가로로 스크롤한다
  if (!bordered) return <div className="w-full overflow-x-auto">{table}</div>;
  return (
    <div className="w-full overflow-x-auto rounded-16 border border-line-grey">
      {table}
    </div>
  );
}

/** Figma Cell "담당자" — 업체명(accent, Body 14/M) + 담당자명(Body 13/R · tertiary) 2줄 */
export function TableUserCell({
  companyName,
  representativeName,
}: {
  companyName: ReactNode;
  representativeName: ReactNode;
}) {
  return (
    <div className="flex flex-col">
      <span className="text-body-14 font-medium text-fg-accent">
        {companyName}
      </span>
      <span className="text-body-13 font-regular text-fg-tertiary">
        {representativeName}
      </span>
    </div>
  );
}
